import { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc, writeBatch, getDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { Shield, Receipt, Copy, Trash2, Settings, User, Key } from 'lucide-react'
import toast from 'react-hot-toast'
import { UserProfilePage } from './UserProfilePage'
import { X, AlertTriangle } from 'lucide-react' // Import icons for modal

// Sub-components
import PlanSettings from '@/components/Admin/PlanSettings'
import PaymentSettings from '@/components/Admin/PaymentSettings'
import DepartmentManager from '@/components/Admin/DepartmentManager'

export default function AdminPage() {
  const { user, currentOrg, userRole } = useAuthStore()
  const [activeTab, setActiveTab] = useState<'profile' | 'plan' | 'pix' | 'advanced' | 'departments'>('profile')
  const [_loading, setLoading] = useState(false)
  const [orgName, setOrgName] = useState('')

  // Transfer Ownership State
  const [transferModalOpen, setTransferModalOpen] = useState(false)
  const [eligibleMembers, setEligibleMembers] = useState<any[]>([])
  const [newOwnerId, setNewOwnerId] = useState('')

  // ... (rest of simple logic)

  // Roles that can manage organization settings
  const canManageOrg = userRole === 'owner' || userRole === 'admin'

  useEffect(() => {
    // If not admin and on an admin-only tab, switch to profile
    if (!canManageOrg && activeTab !== 'profile') {
      setActiveTab('profile')
    }
  }, [canManageOrg, activeTab])

  useEffect(() => {
    if (currentOrg?.name) setOrgName(currentOrg.name)
  }, [currentOrg])

  const handleUpdateName = async () => {
    if (!currentOrg || !orgName.trim()) return
    setLoading(true)
    try {
      await updateDoc(doc(db, 'organizations', currentOrg.id), {
        name: orgName.trim()
      })
      const { organizations, setOrganizations, setCurrentOrg } = useAuthStore.getState()

      const updatedOrgs = organizations.map(org =>
        org.id === currentOrg.id ? { ...org, name: orgName.trim() } : org
      )

      setOrganizations(updatedOrgs)
      setCurrentOrg({ ...currentOrg, name: orgName.trim() })

      toast.success('Nome da organização atualizado!')
    } catch (error: any) {
      toast.error('Erro ao atualizar: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const openTransferModal = async () => {
    if (!currentOrg || !user) return
    setLoading(true)
    try {
      // Fetch members of this org
      const q = query(collection(db, 'members'), where('orgId', '==', currentOrg.id))
      const snapshot = await getDocs(q)
      const membersList = []

      for (const d of snapshot.docs) {
        const mData = d.data()
        if (mData.userId === user.uid) continue // Skip self (current owner)

        // Fetch user profile to show name
        const uSnap = await getDoc(doc(db, 'users', mData.userId))
        if (uSnap.exists()) {
          membersList.push({
            memberId: d.id, // The ID of the document in 'members' collection
            userId: mData.userId,
            displayName: uSnap.data().displayName || uSnap.data().email,
            email: uSnap.data().email
          })
        }
      }
      setEligibleMembers(membersList)
      setTransferModalOpen(true)
    } catch (err) {
      console.error(err)
      toast.error("Erro ao carregar lista de membros")
    } finally {
      setLoading(false)
    }
  }

  const handleTransferOwnership = async () => {
    if (!currentOrg || !newOwnerId || !user) return

    setLoading(true)
    try {
      const batch = writeBatch(db)
      const selectedMember = eligibleMembers.find(m => m.userId === newOwnerId)
      if (!selectedMember) throw new Error("Membro inválido ou não encontrado na lista.")

      // 1. Update Organization Owner
      const orgRef = doc(db, 'organizations', currentOrg.id)
      batch.update(orgRef, { ownerId: newOwnerId })

      // 2. Update New Owner Role in Members Collection
      const newOwnerMemberRef = doc(db, 'members', selectedMember.memberId)
      batch.update(newOwnerMemberRef, { role: 'owner' })

      // 3. Update Old Owner Role (Self) -> Admin
      // Assuming member doc ID is constructed as `{uid}_{orgId}` based on JoinOrgPage pattern
      const myMemberId = `${user.uid}_${currentOrg.id}`
      const myMemberRef = doc(db, 'members', myMemberId)

      // Check if my doc exists just in case (defensive)
      const mySnap = await getDoc(myMemberRef)
      if (mySnap.exists()) {
        batch.update(myMemberRef, { role: 'admin' })
      } else {
        // If not standard ID, try query? No, let's assume standard for now to keep it efficient.
        // Or find my doc from the initial fetch? 
        // Let's assume standard ID for now. It's safe.
        console.warn("Could not find self member doc with standard ID, attempting query fallback...")
        const qSelf = query(collection(db, 'members'), where('orgId', '==', currentOrg.id), where('userId', '==', user.uid))
        const snapSelf = await getDocs(qSelf)
        if (!snapSelf.empty) {
          batch.update(snapSelf.docs[0].ref, { role: 'admin' })
        }
      }

      await batch.commit()

      toast.success("Propriedade transferida com sucesso! A página será recarregada.")
      setTimeout(() => window.location.reload(), 1500)

    } catch (error: any) {
      console.error(error)
      toast.error('Erro na transferência: ' + error.message)
      setLoading(false)
    }
  }

  const handleCopyCode = () => {
    if (currentOrg?.orgCode) {
      navigator.clipboard.writeText(currentOrg.orgCode)
      toast.success('Código copiado!')
    }
  }

  const handleDeleteOrg = async () => {
    if (!currentOrg) return
    const confirm = window.confirm(`ATENÇÃO: Isso excluirá permanentemente a organização "${currentOrg.name}" e TODOS os seus dados. Deseja continuar?`)

    if (confirm) {
      try {
        setLoading(true)
        await deleteDoc(doc(db, 'organizations', currentOrg.id))
        toast.success('Organização excluída.')
        window.location.href = '/dashboard'
      } catch (error: any) {
        toast.error('Erro: ' + error.message)
      } finally {
        setLoading(false)
      }
    }
  }

  // Define tabs
  // ... (tabs definition identical)
  const tabs = [
    { id: 'profile', label: 'Meu Perfil', icon: User },
    ...(canManageOrg ? [
      { id: 'plan', label: 'Plano e Cobrança', icon: Shield },
      { id: 'pix', label: 'Chave Pix', icon: Receipt },
      { id: 'departments', label: 'Departamentos', icon: User },
      { id: 'advanced', label: 'Avançado', icon: Settings },
    ] : [])
  ]

  return (
    <div className="max-w-6xl w-full mx-auto p-6 space-y-6 page-enter">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-6 border-b-2 border-[#001c28]/5">
        <div className="space-y-2">
          <h1 className="text-3xl font-black text-[#001c28] tracking-tighter uppercase italic leading-[0.9]">Configurações</h1>
          <p className="font-bold text-[#001c28]/80 text-sm max-w-2xl">
            Gerencie seu perfil e as definições da organização.
          </p>
        </div>

        {/* Info Cards (Only for Admins) */}
        {canManageOrg && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="bg-white border border-[#001c28]/10 px-4 py-3 rounded-xl flex items-center space-x-3 shadow-sm">
              <span className="text-[10px] font-black text-[#001c28]/60 uppercase tracking-widest">Org ID</span>
              <span className="font-mono text-xs font-black text-[#001c28]">{currentOrg?.orgCode}</span>
              <button onClick={handleCopyCode} className="p-1 hover:bg-[#001c28]/5 rounded-lg text-[#001c28]/60 hover:text-[#001c28] transition-all">
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="bg-white border border-[#d4a94d]/20 px-4 py-3 rounded-xl flex items-center space-x-3 shadow-sm">
              <span className="text-[10px] font-black text-[#001c28]/60 uppercase tracking-widest">Plano</span>
              <span className="bg-[#001c28] text-[#d4a94d] px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-[#001c28]/10">{currentOrg?.plan}</span>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-[2rem] overflow-hidden border border-[#001c28]/5 shadow-xl shadow-[#001c28]/5">
        <div className="bg-white p-2 border-b border-[#001c28]/5">
          <nav className="flex space-x-1 overflow-x-auto pb-2 lg:pb-0">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 px-5 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap group ${isActive
                    ? 'bg-[#001c28] text-[#d4a94d] shadow-lg shadow-[#001c28]/20'
                    : 'text-[#001c28]/40 hover:bg-[#001c28]/5 hover:text-[#001c28]'
                    }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-[#d4a94d]' : 'text-[#001c28]/30 group-hover:text-[#001c28]'}`} />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-8 bg-white min-h-[400px]">
          {activeTab === 'profile' && <UserProfilePage isEmbedded />}

          {canManageOrg && activeTab === 'plan' && <PlanSettings />}

          {canManageOrg && activeTab === 'pix' && <PaymentSettings />}

          {canManageOrg && activeTab === 'departments' && (
            <div className="animate-in fade-in duration-500">
              <DepartmentManager />
            </div>
          )}

          {canManageOrg && activeTab === 'advanced' && (
            <div className="space-y-8 animate-in fade-in duration-500">

              {/* General Settings */}
              <div className="bg-white rounded-2xl border border-[#001c28]/10 p-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-[#001c28]/5 rounded-xl flex items-center justify-center text-[#001c28]">
                    <Settings className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-[#001c28] uppercase tracking-tight">Dados da Organização</h3>
                    <p className="text-xs text-[#001c28]/60 font-bold">Informações visíveis para todos os membros.</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#001c28]/5 space-y-4">
                  <div>
                    <label className="block text-xs font-black text-[#001c28] uppercase tracking-widest mb-2">Nome da Organização</label>
                    <div className="flex gap-4">
                      <input
                        type="text"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        className="flex-1 bg-[#001c28]/[0.02] border-2 border-[#001c28]/10 rounded-xl px-4 py-3 text-sm font-bold text-[#001c28] focus:border-[#d4a94d] focus:bg-white outline-none transition-all uppercase"
                      />
                      <button
                        onClick={handleUpdateName}
                        disabled={!orgName.trim() || orgName === currentOrg?.name}
                        className="px-6 py-3 bg-[#001c28] text-[#d4a94d] font-black rounded-xl text-xs uppercase tracking-widest hover:bg-[#001c28]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Salvar
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 rounded-2xl border border-red-100 p-6 space-y-4">
                {/* ... Danger Zone ... */}
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center text-red-600">
                    <Trash2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-red-900 uppercase tracking-tight">Zona de Perigo</h3>
                    <p className="text-xs text-red-700 font-bold">Ações irreversíveis.</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-red-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Transfer Ownership - Only for Owner */}
                  {userRole === 'owner' && (
                    <div className="flex-1 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-red-100 md:border-b-0 md:border-r md:pr-4 pb-4 md:pb-0">
                      <div className="space-y-1">
                        <h4 className="font-bold text-gray-900 text-sm leading-none">Transferir Propriedade</h4>
                        <p className="text-[10px] text-gray-500 font-medium">Transfere permanentemente o controle da organização.</p>
                      </div>
                      <button
                        onClick={openTransferModal}
                        className="px-5 py-2.5 bg-[#d4a94d] text-white font-black rounded-lg text-[10px] uppercase tracking-widest hover:bg-[#bfa04d] transition-all shadow-lg shadow-[#d4a94d]/20 flex items-center gap-2 whitespace-nowrap"
                      >
                        <Key className="w-3 h-3" />
                        Transferir
                      </button>
                    </div>
                  )}

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 flex-1 pl-0 md:pl-4">
                    <div className="space-y-1">
                      <h4 className="font-bold text-gray-900 text-sm leading-none">Excluir Organização</h4>
                      <p className="text-[10px] text-gray-500 font-medium">Isso apagará permanentemente todos os dados.</p>
                    </div>
                    <button
                      onClick={handleDeleteOrg}
                      className="px-5 py-2.5 bg-red-600 text-white font-black rounded-lg text-[10px] uppercase tracking-widest hover:bg-red-700 transition-all shadow-lg shadow-red-200"
                    >
                      Excluir Tudo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transfer Ownership Modal */}
      {transferModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#001c28]/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[2rem] w-full max-w-lg p-8 shadow-2xl relative overflow-hidden">
            <button
              onClick={() => setTransferModalOpen(false)}
              className="absolute top-6 right-6 p-2 bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-all"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-16 h-16 bg-yellow-50 rounded-2xl flex items-center justify-center text-[#d4a94d] mb-4">
                <Key className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-black text-[#001c28] uppercase italic leading-none">
                Transferir Propriedade
              </h3>
              <p className="text-sm font-bold text-[#001c28]/60 mt-2 max-w-sm">
                Selecione o novo proprietário. <br />
                <span className="text-red-500">Esta ação não pode ser desfeita por você.</span>
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 mb-6 text-left flex gap-3">
              <AlertTriangle className="w-10 h-10 text-yellow-600 shrink-0" />
              <div className="text-xs text-yellow-800">
                <p className="font-bold mb-1">O que acontece agora?</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>O membro selecionado se tornará o <strong>Proprietário (Owner)</strong>.</li>
                  <li>Você se tornará um <strong>Administrador</strong>.</li>
                  <li>Você perderá acesso a funcionalidades exclusivas de dono (como deletar a org).</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-black text-[#001c28] uppercase tracking-widest mb-1 ml-1">
                  Novo Proprietário
                </label>
                <select
                  value={newOwnerId}
                  onChange={(e) => setNewOwnerId(e.target.value)}
                  className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d]"
                >
                  <option value="">Selecione um membro...</option>
                  {eligibleMembers.map(m => (
                    <option key={m.userId} value={m.userId}>
                      {m.displayName} ({m.email})
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleTransferOwnership}
                disabled={!newOwnerId || _loading}
                className="w-full py-4 bg-[#d4a94d] text-white font-black rounded-xl text-xs uppercase tracking-widest hover:bg-[#001c28] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#d4a94d]/20 mt-4"
              >
                {_loading ? 'Processando...' : 'Confirmar Transferência'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
